import { useState } from 'react'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Plus, Pencil, Loader2 } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { useLeadSourcesTab } from '@/hooks/useLeadSourcesTab'
import { LeadSourceCreateModal } from '@/components/Configuracao/LeadSourceCreateModal'
import { getLeadSourceTypeLabel } from '@/components/Configuracao/leadSourceConstants'

export function LeadSourceTab() {
  const { user } = useAuthStore()
  const isAdmin = user?.role?.toLowerCase() === 'admin'
  const {
    sources,
    loading,
    saving,
    showCreateModal,
    setShowCreateModal,
    createSource,
    renameSource,
    toggleActive,
  } = useLeadSourcesTab(isAdmin)

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')

  const startEdit = (id: string, currentName: string) => {
    if (!isAdmin) return
    setEditingId(id)
    setEditValue(currentName)
  }

  const commitEdit = async (id: string) => {
    if (!editValue.trim()) {
      setEditingId(null)
      return
    }
    const ok = await renameSource(id, editValue)
    if (ok) setEditingId(null)
  }

  return (
    <>
      <div className='glass-card p-0 overflow-hidden'>
        <div className='flex items-center justify-between px-4 py-3 border-b border-[var(--glass-border)]'>
          <h3 className='font-semibold text-[var(--font-color-h3)]' style={{ fontSize: 'var(--font-size-h3)' }}>
            Origens de Lead
          </h3>
          {isAdmin && (
            <Button
              variant='primary'
              size='sm'
              icon={<Plus size={13} />}
              onClick={() => setShowCreateModal(true)}
            >
              Nova Origem
            </Button>
          )}
        </div>

        <div className='overflow-x-auto'>
          <table className='w-full'>
            <thead>
              <tr className='border-b border-[var(--glass-border)]'>
                {['Origem', 'Tipo', 'Status', 'Leads', 'Taxa Conversão', 'Ações'].map((h) => (
                  <th
                    key={h}
                    className='text-left px-4 py-3 text-[var(--font-color-label)] font-medium uppercase tracking-wider whitespace-nowrap'
                    style={{ fontSize: 'var(--font-size-label)' }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className='px-4 py-8 text-center text-text-muted text-sm'>
                    <Loader2 size={18} className='inline animate-spin mr-2' />
                    Carregando origens...
                  </td>
                </tr>
              ) : sources.length === 0 ? (
                <tr>
                  <td colSpan={6} className='px-4 py-8 text-center text-text-muted text-sm'>
                    Nenhuma origem cadastrada.
                    {isAdmin ? ' Clique em Nova Origem para começar.' : ''}
                  </td>
                </tr>
              ) : (
                sources.map((source) => (
                  <tr
                    key={source.id}
                    className='border-b border-[var(--glass-border)] last:border-0 hover:bg-[var(--glass-bg)] transition-colors'
                  >
                    <td className='px-4 py-3'>
                      {editingId === source.id ? (
                        <input
                          autoFocus
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={() => commitEdit(source.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') void commitEdit(source.id)
                            if (e.key === 'Escape') setEditingId(null)
                          }}
                          className='bg-[var(--glass-bg)] border border-[var(--brand-primary)] text-[var(--text-primary)] rounded-md px-2 py-1 text-[13px] outline-none min-h-[44px]'
                        />
                      ) : (
                        <div>
                          <span className='font-medium text-[var(--text-primary)] text-[13px] block'>
                            {source.name}
                          </span>
                          {source.description && (
                            <span className='text-[11px] text-text-muted line-clamp-1'>
                              {source.description}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className='px-4 py-3'>
                      <span className='text-[var(--text-primary)] text-[12px]'>
                        {getLeadSourceTypeLabel(source.type)}
                      </span>
                    </td>
                    <td className='px-4 py-3'>
                      <button
                        type='button'
                        onClick={() => toggleActive(source.id)}
                        disabled={!isAdmin}
                        className='min-h-[44px] flex items-center disabled:opacity-60'
                      >
                        <Badge variant={source.active ? 'success' : 'muted'}>
                          {source.active ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </button>
                    </td>
                    <td className='px-4 py-3'>
                      <span className='text-[var(--text-primary)] text-[13px]'>{source.leadCount}</span>
                    </td>
                    <td className='px-4 py-3'>
                      <span className='text-[var(--text-primary)] text-[13px]'>{source.conversionRate}%</span>
                    </td>
                    <td className='px-4 py-3'>
                      {isAdmin && (
                        <button
                          type='button'
                          onClick={() => startEdit(source.id, source.name)}
                          className='p-2 rounded-md hover:bg-[var(--glass-bg)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center'
                        >
                          <Pencil size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <LeadSourceCreateModal
        open={showCreateModal}
        saving={saving}
        onClose={() => setShowCreateModal(false)}
        onSave={createSource}
      />
    </>
  )
}
